import { useEffect, useMemo, useState } from "react";
import { Chessboard } from "react-chessboard";
import type { ChessboardOptions } from "react-chessboard";
import { applyUciMove } from "@onchainreps/chess";
import type { LocalStore, StoredRepertoire } from "@onchainreps/storage";

const START_FEN = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1";

interface RepertoireBuilderProps {
  store: LocalStore | null;
  onTrain: (lines: StoredRepertoire[]) => void;
}

export function RepertoireBuilder({ store, onTrain }: RepertoireBuilderProps) {
  const [color, setColor] = useState<"white" | "black">("white");
  const [fen, setFen] = useState(START_FEN);
  const [moves, setMoves] = useState<string[]>([]);
  const [name, setName] = useState("");
  const [lines, setLines] = useState<StoredRepertoire[]>([]);
  const [message, setMessage] = useState<string | null>(null);

  const refresh = async () => {
    if (!store) return;
    setLines(await store.listRepertoires());
  };

  useEffect(() => {
    void refresh();
  }, [store]);

  const reset = () => {
    setFen(START_FEN);
    setMoves([]);
    setMessage(null);
  };

  const options: ChessboardOptions = useMemo(
    () => ({
      position: fen,
      boardOrientation: "white",
      allowDragging: true,
      showNotation: true,
      animationDurationInMs: 160,
      darkSquareStyle: { backgroundColor: "var(--board-dark)" },
      lightSquareStyle: { backgroundColor: "var(--board-light)" },
      boardStyle: { borderRadius: "6px", boxShadow: "none" },
      onPieceDrop: ({ sourceSquare, targetSquare }) => {
        if (!sourceSquare || !targetSquare) return false;
        const applied = applyUciMove(fen, `${sourceSquare}${targetSquare}`);
        if (!applied.legal) return false;
        setFen(applied.fen);
        setMoves((current) => [...current, applied.san]);
        return true;
      },
    }),
    [fen],
  );

  const save = async () => {
    if (!store || moves.length < 2) return;
    const now = new Date().toISOString();
    const line: StoredRepertoire = {
      id: `rep-${Date.now()}`,
      name: name.trim() || `Line ${new Date().toLocaleDateString()}`,
      color,
      moves: [...moves],
      createdAt: now,
      updatedAt: now,
    };
    await store.putRepertoire(line);
    setMessage(`Saved “${line.name}”.`);
    setName("");
    reset();
    await refresh();
  };

  const remove = async (id: string) => {
    if (!store) return;
    await store.deleteRepertoire(id);
    await refresh();
  };

  const own = lines.filter((line) => line.color === color);

  return (
    <div className="repertoire">
      <div className="repertoire__head">
        <div className="segmented" role="tablist">
          {(["white", "black"] as const).map((side) => (
            <button
              key={side}
              role="tab"
              aria-selected={color === side}
              onClick={() => {
                setColor(side);
                reset();
              }}
            >
              {side === "white" ? "As White" : "As Black"}
            </button>
          ))}
        </div>
        <p className="planned__desc">
          Play the moves you want to keep. As White the trainer asks for your moves; as Black it
          plays the opponent's book and asks for your replies.
        </p>
      </div>

      <div className="repertoire__board">
        <Chessboard options={options} />
      </div>

      <div className="repertoire__moves">
        {moves.length ? (
          moves
            .map((san, index) => ({
              san,
              label: index % 2 === 0 ? `${index / 2 + 1}.` : `${(index + 1) / 2}...`,
            }))
            .map((move, index) => (
              <span className="repertoire__move" key={`${move.san}-${index}`}>
                <span className="repertoire__move-label">{move.label}</span>
                {move.san}
              </span>
            ))
        ) : (
          <span className="notice">Drag a piece to start the line.</span>
        )}
      </div>

      <div className="repertoire__actions">
        <input
          className="input"
          placeholder="Name this line"
          value={name}
          onChange={(event) => setName(event.target.value)}
          aria-label="Name this line"
        />
        <button className="btn btn--ghost" onClick={reset} disabled={!moves.length}>
          Clear
        </button>
        <button className="btn btn--primary" onClick={() => void save()} disabled={moves.length < 2}>
          Save line
        </button>
      </div>

      {message ? <p className="notice">{message}</p> : null}

      <div className="repertoire__list">
        <div className="repertoire__list-head">
          <h3 className="progress__title">
            {color === "white" ? "Your lines" : "Opponent lines"} ({own.length})
          </h3>
          {own.length ? (
            <button className="btn btn--primary" onClick={() => onTrain(own)}>
              Train this repertoire
            </button>
          ) : null}
        </div>
        {own.length ? (
          <ul className="opstats">
            {own.map((line) => (
              <li className="opstats__row" key={line.id}>
                <span className="opstats__name">{line.name}</span>
                <span className="opstats__eco">{line.moves.length} moves</span>
                <span className="opstats__value"> </span>
                <button
                  className="iconbtn iconbtn--quiet"
                  aria-label={`Delete ${line.name}`}
                  onClick={() => void remove(line.id)}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="notice">No lines yet. Play a few moves above and save the line.</p>
        )}
      </div>
    </div>
  );
}
